import { plainSpeechText } from "./tts-reply-text";
import {
  completedStreamingGroup,
  ENDS_WITH_LEAD_IN,
  ENDS_WITH_TERMINATOR,
  hasSpeech,
  isListNumberToken,
  openingClause,
  splitSpeechSegments,
} from "./tts-speech-segments";

export type StreamingSpeak = {
  text: string;
  spokenIndex: number;
  openingGroupQueued: boolean;
  /** The assistant message this state follows, once one is adopted. */
  streamId?: string;
  /** Messages this execution already moved past. They never come back. */
  retiredStreamIds: Set<string>;
  /** An earlier message of this execution already queued speech. */
  earlierSpeechQueued: boolean;
};

export type StreamingMessageSwitch =
  | { kind: "same" }
  | { kind: "retired" }
  | {
      kind: "reset";
      retiredStreamId: string;
      flushed: { text: string; spokenPrefix: string } | null;
    };

export function createStreamingSpeak(): StreamingSpeak {
  return {
    text: "",
    spokenIndex: 0,
    openingGroupQueued: false,
    retiredStreamIds: new Set(),
    earlierSpeechQueued: false,
  };
}

// A replacement message or revised spoken prefix is reconciled by the final
// response; never replay an old snapshot or speak unrelated progress here.
function extendsSpokenPrefix(state: StreamingSpeak, text: string): boolean {
  const queuedPrefix = state.text.slice(0, state.spokenIndex);
  return !queuedPrefix || text.startsWith(queuedPrefix);
}

/**
 * Decide which assistant message a streaming call belongs to. A stream id
 * never seen before is a new message: move to it forward-only, handing back
 * the old message's held sentence so it is spoken once. A retired id is a
 * late snapshot. Calls without an id keep the prefix guard alone.
 */
export function switchStreamingMessage(
  state: StreamingSpeak,
  text: string,
  streamId?: string,
): StreamingMessageSwitch {
  if (!streamId || streamId === state.streamId) return { kind: "same" };
  if (state.retiredStreamIds.has(streamId)) return { kind: "retired" };
  if (state.streamId === undefined) {
    // Never a reset from an identity-less state: its text may be this very
    // message, so adopt the id only when the text continues what was spoken.
    if (extendsSpokenPrefix(state, text)) state.streamId = streamId;
    return { kind: "same" };
  }
  const flushedText = forceAdvanceStreamingReply(state);
  const flushed = flushedText
    ? {
        text: flushedText,
        spokenPrefix: state.text.slice(0, state.spokenIndex),
      }
    : null;
  const retiredStreamId = state.streamId;
  state.retiredStreamIds.add(retiredStreamId);
  state.earlierSpeechQueued ||= state.openingGroupQueued;
  state.streamId = streamId;
  state.text = "";
  state.spokenIndex = 0;
  state.openingGroupQueued = false;
  return { kind: "reset", retiredStreamId, flushed };
}

/** Speech is already queued, by this message or an earlier one. */
export function hasQueuedStreamingSpeech(state: StreamingSpeak): boolean {
  return state.openingGroupQueued || state.earlierSpeechQueued;
}

/** Advance only stable, completed sentences from the same assistant stream. */
export function advanceStreamingReply(
  state: StreamingSpeak,
  text: string,
): string | null {
  if (!extendsSpokenPrefix(state, text)) return null;
  state.text = text;
  const pending = text.slice(state.spokenIndex);
  const first = state.openingGroupQueued
    ? completedStreamingGroup(pending)
    : (openingClause(pending) ?? completedStreamingGroup(pending));
  if (!first) return null;
  const completed = state.openingGroupQueued
    ? splitSpeechSegments(pending, false)
    : [first];
  const boundary = completed[completed.length - 1] ?? first;
  const group = plainSpeechText(pending.slice(0, boundary.consumed));
  if (!group) return null;
  state.spokenIndex += boundary.consumed;
  state.openingGroupQueued = true;
  return group;
}

/**
 * A complete sentence held back only by the trailing-period ambiguity guard
 * (e.g. a single-sentence commentary ending in "." that could, in theory,
 * still grow into "word.suffix"), or a lead-in ending in ":" or a dash
 * ("Let me check:"). Callers flush it after a short settle delay when no more
 * stream text arrives, or when the next message starts — otherwise
 * commentary before tool calls stays visible but silent.
 */
export function heldTrailingSentence(
  pending: string,
): { text: string; consumed: number } | null {
  if (!pending.trim() || completedStreamingGroup(pending)) return null;
  if (ENDS_WITH_LEAD_IN.test(pending)) {
    return hasSpeech(pending)
      ? { text: pending.trim(), consumed: pending.length }
      : null;
  }
  if (!ENDS_WITH_TERMINATOR.test(pending)) return null;
  const first = splitSpeechSegments(pending, true)[0];
  if (!first || isListNumberToken(first.text) || !hasSpeech(first.text)) {
    return null;
  }
  if (!ENDS_WITH_TERMINATOR.test(first.text)) return null;
  return first;
}

/** Force-speak the held trailing sentence (settle-delay path). */
export function forceAdvanceStreamingReply(
  state: StreamingSpeak,
): string | null {
  const pending = state.text.slice(state.spokenIndex);
  const held = heldTrailingSentence(pending);
  if (!held) return null;
  const group = plainSpeechText(pending.slice(0, held.consumed));
  if (!group) return null;
  state.spokenIndex += held.consumed;
  state.openingGroupQueued = true;
  return group;
}
